/** Shared, dependency-free contract for the Appraisal workspace and its rules. */
export const APPRAISAL_WORKSPACE_FLAG = 'appraisal_workspace_v1';
export const APPRAISAL_EXTRACTION_VERSION = 'mismo-2.6-v1';
export const APPRAISAL_RULE_VERSION = '1004d-v1';
export const APPRAISAL_XML_MAX_BYTES = 64 * 1024 * 1024;
export const APPRAISAL_INLINE_MAX_BYTES = 25 * 1024 * 1024;
export type AppraisalMode = 'off' | 'assessment' | 'live';
export type AppraisalConclusion = 'as_is' | 'completion' | 'repairs' | 'mixed' | 'inspection' | 'subject_to_unknown' | 'unclear';
export type AppraisalOrderKind = 'full' | 'repair_inspection' | 'recertification' | 'unknown';

export interface AppraisalEligibilityLoan {
  trans_details_loan_number?: string | null;
  loan_folder?: string | null;
  funded_date?: string | null;
}

export function appraisalEligibility(loan: AppraisalEligibilityLoan | null | undefined): { eligible: boolean; reason: string | null } {
  if (!loan || !loan.trans_details_loan_number || !loan.loan_folder || loan.funded_date === undefined) {
    return { eligible: false, reason: 'Loan eligibility could not be verified.' };
  }
  if (loan.loan_folder !== 'Active') {
    return { eligible: false, reason: 'Available for Active loans only.' };
  }
  if (!loan.trans_details_loan_number.startsWith('LW')) {
    return { eligible: false, reason: 'Available for LW loans only.' };
  }
  if (loan.funded_date) {
    return { eligible: false, reason: 'This loan has funded. Saved appraisal information is read-only.' };
  }
  return { eligible: true, reason: null };
}

export interface AppraisalFacts {
  format: string;
  supported: boolean;
  extractionVersion: string;
  property: { street: string | null; unit: string | null; city: string | null; state: string | null; postalCode: string | null };
  appraiser: { name: string | null; company: string | null; license: string | null; licenseState: string | null; licenseExpires: string | null };
  form: string | null;
  fileNumber: string | null;
  /** Older stored reports have not yet been re-parsed. */
  agencyCaseNumber?: string | null;
  effectiveDate: string | null;
  signedDate: string | null;
  value: number | null;
  location: 'Urban' | 'Suburban' | 'Rural' | null;
  conclusion: AppraisalConclusion;
  conclusionValues: string[];
  requirements: string | null;
  warnings: string[];
}

export interface AppraisalCheck {
  rule_code: '1004d';
  rule_version: string;
  extraction_version: string;
  outcome: 'passed' | 'required' | 'review' | 'not_applicable';
  headline: string;
  reason: string;
  condition_code: '0800' | '0801' | null;
  requirements: string | null;
  evidence: { conclusionValues: string[]; requirements: string | null };
}

export function auditAppraisal(facts: AppraisalFacts, kind: AppraisalOrderKind, identityIssues: string[] = []): AppraisalCheck {
  const base = {
    rule_code: '1004d' as const,
    rule_version: APPRAISAL_RULE_VERSION,
    extraction_version: facts.extractionVersion,
    condition_code: null,
    requirements: facts.requirements,
    evidence: { conclusionValues: facts.conclusionValues, requirements: facts.requirements }
  };
  const review = (reason: string): AppraisalCheck => ({ ...base, outcome: 'review', headline: 'Appraisal needs review', reason });
  if (!facts.supported) {
    return review('This XML format is not supported yet. Review the original report.');
  }
  if (identityIssues.length) {
    return review(identityIssues.join(' '));
  }
  if (kind === 'repair_inspection' || kind === 'recertification' || /1004D|442/.test(facts.form || '')) {
    return { ...base, outcome: 'not_applicable', headline: 'Completion or update report', reason: 'This report does not create or clear a 1004D condition.' };
  }
  if (kind === 'unknown' && classifyAppraisalOrder(facts.form) !== 'full') {
    return review('This XML could not be identified as a supported full appraisal report.');
  }
  if (facts.conclusion === 'inspection') {
    return review('A specialist inspection is required. The processor must review the requirement before deciding on a 1004D or fee redisclosure.');
  }
  if (facts.conclusion === 'unclear' || facts.warnings.length) {
    return review(facts.warnings.join(' ') || 'The appraisal conclusion is missing or contradictory.');
  }
  if (facts.conclusion === 'as_is') {
    return { ...base, outcome: 'passed', headline: 'As is — no 1004D required', reason: 'The appraisal is completed As Is. No condition or email is needed.' };
  }
  const code = facts.conclusion === 'completion' && facts.requirements ? '0800' : '0801';
  return {
    ...base,
    outcome: 'required',
    headline: '1004D required',
    reason: code === '0800' ? 'Subject to completion of construction.' : 'Subject to repairs, mixed requirements, or reasons requiring appraisal review.',
    condition_code: code,
    requirements: facts.requirements || 'See appraisal for reasons.'
  };
}

export function classifyAppraisalOrder(productCode: string | null | undefined): AppraisalOrderKind {
  const code = productCode || '';
  if (/1004D|442|appraisal update/i.test(code)) {
    if (/recert|update of value/i.test(code) && !/repair|completion/i.test(code)) { return 'recertification'; }
    if (/inspection|repair|completion/i.test(code)) { return 'repair_inspection'; }
    return 'unknown';
  }
  return /uniform residential appraisal|individual condominium|small residential income|manufactured home appraisal|\b(?:FNM)?(1004|1073|1025|1004C|2055|2090)\b/i.test(code) ? 'full' : 'unknown';
}

/** A correction is an action on an order, not a new assignment. Never infer one from file bytes alone. */
export function isDistinctAppraisalAssignment(current: {
  orderId: string | null; kind: AppraisalOrderKind; action: string | null;
  reportIdentity: string | null; pdfHash: string | null;
}, prior: { orderId: string | null; reportIdentity: string | null; pdfHash: string | null } | null): boolean {
  return Boolean(prior && current.kind === 'full' && current.orderId && prior.orderId &&
    current.orderId !== prior.orderId && !/correction|revision/i.test(current.action || '') &&
    current.reportIdentity && prior.reportIdentity && current.reportIdentity !== prior.reportIdentity &&
    current.pdfHash && prior.pdfHash && current.pdfHash !== prior.pdfHash);
}

export function appraisalIsFindingsOnly(deliveredAt: string | null, liveAfter: string | null, mode: AppraisalMode): boolean {
  if (mode !== 'live' || !deliveredAt || !liveAfter) { return true; }
  const delivered = Date.parse(deliveredAt);
  const boundary = Date.parse(liveAfter);
  return !Number.isFinite(delivered) || !Number.isFinite(boundary) || delivered <= boundary;
}

export function appraisalReportPath(loanId: string, reportId?: string): string {
  const params = new URLSearchParams({ section: 'loan-center', loan: loanId, tab: 'appraisal' });
  if (reportId) { params.set('report', reportId); }
  return `/internal?${params.toString()}`;
}

export interface AppraisalOrder {
  id: string;
  service_order_id: string;
  transaction_id: string | null;
  provider: string | null;
  product_code: string | null;
  request_action: string | null;
  kind: AppraisalOrderKind;
  status: string | null;
  ordered_at: string | null;
  accepted_at: string | null;
  inspection_at: string | null;
  inspection_display: string | null;
  requested_due_date: string | null;
  first_received_at: string | null;
  latest_received_at: string | null;
  refreshed_at: string | null;
  refresh_requested_at: string | null;
  refresh_error: string | null;
}

export interface AppraisalResource { id: string; name: string; mimeType: string | null }
export interface AppraisalEvent {
  id: string;
  order_id: string;
  event_id: string;
  kind: 'message' | 'update' | 'delivery' | 'system';
  sender: string | null;
  occurred_at: string;
  text: string;
  comments: string | null;
  resources: AppraisalResource[];
}

export interface AppraisalReport {
  id: string;
  order_id: string | null;
  source: 'mercury' | 'upload' | 'attachment';
  source_name: string;
  status: string;
  received_at: string | null;
  created_at: string;
  updated_at: string;
  findings_only: boolean;
  facts: AppraisalFacts | null;
  condition_state: string;
  condition_code: string | null;
  condition_id: string | null;
  condition_prior_to: string | null;
  email_state: string;
  email_sent_at: string | null;
  delivery_method: 'attachment' | 'loan_center_link' | null;
  last_error: string | null;
  resolution_note: string | null;
  resolved_at: string | null;
  has_xml: boolean;
  has_pdf: boolean;
  checks: AppraisalCheck[];
}

export interface AppraisalSnapshot {
  mode: AppraisalMode;
  eligible: boolean;
  eligibilityReason: string | null;
  canResolve: boolean;
  orders: AppraisalOrder[];
  events: AppraisalEvent[];
  reports: AppraisalReport[];
  refreshedAt: string | null;
  refreshPending: boolean;
  feeCompliance?: import('./invoice-fee-display-contract.ts').InvoiceFeeDisplay[];
  /** Absent while appraisal_payment_links_v1 is off. */
  paymentLinks?: AppraisalPaymentLinks | null;
}

export type AppraisalPaymentSource = 'mercury' | 'email' | 'manual';
export type AppraisalPaymentReminderStatus = 'sending' | 'sent' | 'failed' | 'unknown';
export interface AppraisalPaymentReference { fileId?: string; orderReferenceId?: string; orderNumber?: string }

export interface AppraisalPaymentReminderSummary {
  id: string;
  status: AppraisalPaymentReminderStatus;
  createdAt: string;
  sentAt: string | null;
  senderName: string | null;
  to: string[];
}

/** One payment request bound to this loan. The URL is a bearer link for loan-authorized staff only. */
export interface AppraisalPaymentRequest {
  id: string;
  amcName: string | null;
  url: string;
  host: string;
  reference: AppraisalPaymentReference;
  amount: number | null;
  firstSeenAt: string;
  sources: AppraisalPaymentSource[];
  lastReminder: AppraisalPaymentReminderSummary | null;
  /** A reminder is sending or unconfirmed, so another cannot start. */
  reminderOpen: boolean;
}

export interface AppraisalPaymentLinks {
  reminderEnabled: boolean;
  requests: AppraisalPaymentRequest[];
}

/** A captured email no loan could be bound to automatically. Admins link it from Add payment link. */
export interface AppraisalUnresolvedPaymentLink {
  id: string;
  amcName: string | null;
  host: string;
  receivedAt: string;
  propertyText: string | null;
  borrowerText: string | null;
}

export interface AppraisalPaymentReminderPreview {
  requestId: string;
  to: string[];
  cc: string[];
  bcc: string[];
  replyTo: string;
  /** copied: the LO is on the email; sender: the LO is sending it; not_found: no single active LO user. */
  loanOfficer: 'copied' | 'sender' | 'not_found';
  envelopeKey: string;
  property: string;
  amcName: string | null;
  host: string;
  reference: AppraisalPaymentReference;
}

export interface AppraisalPaymentReminderOutcome {
  status: AppraisalPaymentReminderStatus;
  message: string;
}

/**
 * One row of Company Reports → Appraisal Processes → Payment links: a current
 * link on a loan that can still be sent a reminder, visible only to a viewer
 * who could resend it from the loan's Appraisal tab.
 */
export interface AppraisalPaymentLinksReportRow {
  id: string;
  loanId: string;
  encompassLoanId: string;
  loanNumber: string;
  loanFolder: string;
  fundedDate: string | null;
  borrowerName: string | null;
  /** The street line, and "City, ST ZIP" under it. */
  propertyStreet: string | null;
  propertyLocality: string | null;
  loanOfficerName: string | null;
  amcName: string | null;
  url: string;
  host: string;
  /** A generic payment page also needs these. */
  reference: Pick<AppraisalPaymentReference, 'fileId' | 'orderReferenceId'>;
  firstSeenAt: string;
  reminderOpen: boolean;
  lastReminder: Pick<AppraisalPaymentReminderSummary, 'id' | 'status' | 'createdAt' | 'sentAt' | 'senderName'> | null;
}

export interface AppraisalPaymentLinksReport {
  /** False while appraisal_payment_links_v1 is off; then there are no rows. */
  enabled: boolean;
  reminderEnabled: boolean;
  truncated: boolean;
  rows: AppraisalPaymentLinksReportRow[];
}

/** The newest current link on a loan: what Add payment link's Replace would supersede. Never its URL. */
export type AppraisalCurrentPaymentLink = Pick<AppraisalPaymentRequest, 'id' | 'amcName' | 'host' | 'reminderOpen'>;

/** A loan the viewer could save a payment link to, found by Add payment link in the report. */
export interface AppraisalPaymentLinkLoan {
  loanId: string;
  encompassLoanId: string;
  loanNumber: string;
  borrowerName: string | null;
  propertyStreet: string | null;
  propertyLocality: string | null;
  currentLink: AppraisalCurrentPaymentLink | null;
}

export interface AppraisalPaymentLinkLoanSearch {
  rows: AppraisalPaymentLinkLoan[];
  /** More loans matched than the ten shown. */
  more: boolean;
}
